"use client";

import * as React from "react";
import { Moon, Sun } from "lucide-react";
import { Button } from "@/components/ui/button";
import { useTheme } from "next-themes";
import { Theme } from "@/constants/theme";
import { useHydrated } from "@/lib/use-hydrated";

export function ThemeToggle({
  "aria-label": ariaLabel = "Toggle theme",
  size = "icon",
  variant = "outline",
}: {
  "aria-label"?: string;
  size?: React.ComponentProps<typeof Button>["size"];
  variant?: React.ComponentProps<typeof Button>["variant"];
}) {
  const { resolvedTheme, setTheme } = useTheme();
  const mounted = useHydrated();
  const isDark = resolvedTheme === Theme.DARK;
  const icon = mounted ? (
    isDark ? (
      <Sun className="size-4" data-testid="light-theme-icon" />
    ) : (
      <Moon className="size-4" data-testid="dark-theme-icon" />
    )
  ) : (
    <span className="block size-4" aria-hidden="true" />
  );

  const tooltipText = isDark ? "Switch to light mode" : "Switch to dark mode";

  // The tooltip comes from the page's HoverTooltipDelegate, which reads the
  // attribute when the pointer arrives; only a hydrated button can be
  // hovered, so the pre-hydration text is never shown.
  return (
    <Button
      variant={variant}
      size={size}
      aria-label={ariaLabel}
      data-testid="theme-toggle-button"
      data-tooltip={tooltipText}
      onClick={() => setTheme(isDark ? Theme.LIGHT : Theme.DARK)}
    >
      {icon}
    </Button>
  );
}
